import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { CONTACT_CATEGORIES } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", company: "", category: CONTACT_CATEGORIES[0], message: "" };

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.15em] text-ivory/50">{label}</span>
    <input
      {...props}
      className="mt-2 w-full bg-transparent border-b border-white/20 py-3 text-ivory placeholder-ivory/30 focus:outline-none focus:border-gold transition-colors"
    />
  </label>
);

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your enquiry has been received.");
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-navy text-ivory py-24 lg:py-32 noise-overlay">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>Contact</Overline>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight">KRV Nexus Pvt Ltd</h2>
            <p className="mt-6 text-ivory/60 leading-relaxed">
              For general business, vendor, retail, furniture, education, careers, and media enquiries — reach out to the corporate desk.
            </p>
            <div className="mt-10 space-y-5">
              <div className="flex items-center gap-4"><Mail size={18} className="text-gold" /><span className="text-ivory/70" data-testid="contact-email">contact@krvnexus.com</span></div>
              <div className="flex items-center gap-4"><Phone size={18} className="text-gold" /><span className="text-ivory/70" data-testid="contact-phone">+91 XXXXX XXXXX</span></div>
              <div className="flex items-start gap-4"><MapPin size={18} className="text-gold mt-1" /><span className="text-ivory/70" data-testid="contact-address">Registered Office — Address to be updated<br />India</span></div>
            </div>
            <p className="mt-8 text-xs text-ivory/30">Contact details shown are placeholders and will be updated with official information.</p>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="border border-white/10 bg-midnight/50 p-8 lg:p-10 space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Full Name" data-testid="contact-input-name" required value={form.name} onChange={update("name")} placeholder="Your name" />
                <Field label="Email" type="email" data-testid="contact-input-email" required value={form.email} onChange={update("email")} placeholder="you@company.com" />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Phone" data-testid="contact-input-phone" value={form.phone} onChange={update("phone")} placeholder="+91 ..." />
                <Field label="Company / Organization" data-testid="contact-input-company" value={form.company} onChange={update("company")} placeholder="Optional" />
              </div>

              <label className="block">
                <span className="text-xs uppercase tracking-[0.15em] text-ivory/50">Enquiry Type</span>
                <select
                  data-testid="contact-select-category"
                  value={form.category}
                  onChange={update("category")}
                  className="mt-2 w-full bg-midnight border-b border-white/20 py-3 text-ivory focus:outline-none focus:border-gold transition-colors"
                >
                  {CONTACT_CATEGORIES.map((c) => <option key={c} value={c} className="bg-midnight">{c}</option>)}
                </select>
              </label>

              <label className="block">
                <span className="text-xs uppercase tracking-[0.15em] text-ivory/50">Message</span>
                <textarea
                  data-testid="contact-input-message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your enquiry"
                  className="mt-2 w-full bg-transparent border-b border-white/20 py-3 text-ivory placeholder-ivory/30 focus:outline-none focus:border-gold transition-colors resize-none"
                />
              </label>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 bg-gold text-navy px-8 py-4 rounded-full font-medium hover:bg-gold-muted transition-colors disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send Enquiry"}
                <Send size={16} />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
